import { forwardRef } from "react";
import Select, {
  components,
  type InputActionMeta,
  type MenuListProps,
  type Props as SelectProps,
  type SelectInstance,
} from "react-select";

// Types for the select component
export type TSelectOption = {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  [key: string]: unknown;
};

export type TSelectProps = {
  options: TSelectOption[];
  onChange: (value: any) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  value?: TSelectOption | TSelectOption[] | null;
  labelBy?: string;
  name: string;
  placeholder: string | undefined;
  getOptionLabel: (option: TSelectOption) => string;
  getOptionValue: (option: TSelectOption) => string;
  className?: string;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  isMulti?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  menuIsOpen?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  isSearchable?: boolean;
  inputValue?: string;
  onInputChange?: (value: string, actionMeta?: InputActionMeta) => void;
  filterOption?: Parameters<typeof Select>[0]["filterOption"];
  /** Optional overrides for react-select components (e.g. SingleValue for custom value display) */
  components?: SelectProps<TSelectOption>["components"];
  /** Optional style overrides (merged with defaults) */
  styles?: SelectProps<TSelectOption>["styles"];
  /** Tailwind-friendly class names for inner parts (react-select v5+) */
  classNames?: SelectProps<TSelectOption>["classNames"];
  noOptionsMessage?: (obj: { inputValue: string }) => string | null;
};

// Custom MenuList component with loader at the bottom
const CustomMenuList = (props: MenuListProps<TSelectOption>) => {
  const isLoadingMore = Boolean((props.selectProps as any)?.isLoadingMore);

  return (
    <components.MenuList {...props}>
      {props.children}
      {isLoadingMore && (
        <div className="flex items-center justify-center py-2 px-3 border-t border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin"></div>
            <span>Loading...</span>
          </div>
        </div>
      )}
    </components.MenuList>
  );
};

type TSelectWithLoadingMore = SelectProps<TSelectOption, boolean> & {
  isLoadingMore?: boolean;
};

const SelectDropdown = forwardRef<
  SelectInstance<TSelectOption, boolean>,
  TSelectProps
>(
  (
    {
      options,
      onChange,
      onBlur,
      onFocus,
      value,
      labelBy,
      name,
      placeholder,
      getOptionLabel,
      getOptionValue,
      className,
      isLoading,
      isLoadingMore = false,
      isClearable,
      isDisabled,
      isMulti = false,
      hasNextPage,
      onLoadMore,
      menuIsOpen,
      onMenuOpen,
      onMenuClose,
      isSearchable,
      inputValue,
      onInputChange,
      filterOption,
      components: customComponents,
      styles: customStyles,
      classNames: classNamesProp,
      noOptionsMessage,
    },
    ref,
  ) => {
    const handleMenuScrollToBottom = () => {
      if (hasNextPage && onLoadMore && !isLoadingMore) {
        onLoadMore();
      }
    };

    const selectProps: TSelectWithLoadingMore = {
      "aria-labelledby": labelBy,
      classNamePrefix: "react-select",
      className,
      name,
      placeholder,
      options,
      getOptionLabel,
      getOptionValue,
      onChange,
      noOptionsMessage,
      onBlur,
      onFocus,
      value,
      isLoading,
      isLoadingMore,
      isClearable,
      isDisabled,
      isMulti,
      menuPlacement: "auto",
      menuShouldScrollIntoView: false,
      onMenuScrollToBottom: handleMenuScrollToBottom,
      closeMenuOnScroll: false,
      blurInputOnSelect: false,
      menuIsOpen,
      onMenuOpen,
      onMenuClose,
      isSearchable,
      inputValue,
      onInputChange,
      filterOption,
      menuPortalTarget: document.body,
      id: `select-${name}`,
      hideSelectedOptions: false,
      classNames: classNamesProp,
      components: {
        MenuList: CustomMenuList,
        ...(customComponents ?? {}),
      },
      styles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menuList: (base) => ({
          ...base,
          maxHeight: "230px",
          overflow: "auto",
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected
            ? "hsl(var(--primary) / 0.1)"
            : state.isFocused
              ? "hsl(var(--accent))"
              : "transparent",
          color: state.isSelected ? "hsl(var(--primary))" : "inherit",
          cursor: "pointer",
          fontWeight: state.isSelected ? "500" : "400",
          ":active": {
            backgroundColor: "hsl(var(--accent))",
          },
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          maxWidth: "100%",
        }),
        ...(customStyles ?? {}),
      },
    };

    return (
      <Select<TSelectOption, boolean> {...(selectProps as any)} ref={ref} />
    );
  },
);
SelectDropdown.displayName = "SelectDropdown";

export default SelectDropdown;
